import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../../apiConfig';
import Navbar from '../../navbar.jsx';
import Footer from '../../footer.jsx';
import userStore from '../../../store/user.zustand';

const AssessmentDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, setUser, setAuthenticated } = userStore();
  const [assessments, setAssessments] = useState([]);
  const [expandedAssessment, setExpandedAssessment] = useState(null);
  const [attemptsData, setAttemptsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuthentication = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/users/me?_t=${Date.now()}`, {
        withCredentials: true,
        headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' },
      });

      if (response?.data?.success) {
        setUser(response.data.data);
        setAuthenticated(true);
        return true;
      } else {
        setAuthenticated(false);
        return false;
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setAuthenticated(false);
      return false;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const isAuth = await checkAuthentication();
      if (!isAuth) {
        navigate('/login', { state: { from: location.pathname } });
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/v1/admin/users/assessments`, {
          withCredentials: true,
          headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' },
        });

        if (response.data.success) {
          const sortedAssessments = response.data.data.sort((a, b) => {
            const aDate = a.lastAttempt ? new Date(a.lastAttempt) : new Date(0);
            const bDate = b.lastAttempt ? new Date(b.lastAttempt) : new Date(0);
            return bDate - aDate;
          });
          setAssessments(sortedAssessments);
        } else {
          throw new Error(response.data.message || 'Failed to fetch assessments');
        }
      } catch (err) {
        console.error('Fetch assessments error:', err);
        setError(err.message || 'An error occurred while fetching assessments');
        toast.error(err.message || 'An error occurred', { autoClose: 2500 });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate, location.pathname]);

  const fetchAttempts = async (slug) => {
    try {
      const attemptsResponse = await axios.get(
        `${API_URL}/api/v1/admin/public/assessments/${slug}/user-attempts`,
        { withCredentials: true }
      );
      if (attemptsResponse.data.success) {
        setAttemptsData((prev) => ({
          ...prev,
          [slug]: attemptsResponse.data.data,
        }));
      } else {
        throw new Error(attemptsResponse.data.message || 'Failed to fetch attempts');
      }
    } catch (error) {
      console.error('Fetch attempts error:', error);
      toast.error('Failed to fetch attempt details', { autoClose: 2500 });
    }
  };

  const toggleAssessmentAttempts = (slug, attemptsMade) => {
    if (attemptsMade === 0) return;
    if (expandedAssessment === slug) {
      setExpandedAssessment(null);
    } else {
      setExpandedAssessment(slug);
      if (!attemptsData[slug]) {
        fetchAttempts(slug);
      }
    }
  };

  const handleViewAssessment = (slug) => {
    navigate(`/assessments/${slug}`);
  };

  const handleViewResults = (slug, attemptId) => {
    navigate(`/assessments/${slug}/results/${attemptId}`);
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-100">
        <Navbar />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-gray-700 text-xl animate-pulse">Loading...</div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col min-h-screen bg-gray-100">
        <Navbar />
        <main className="flex-grow flex items-center justify-center">
          <div className="bg-red-100 text-red-700 p-6 rounded-xl shadow-home-hero">
            {error}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <Navbar />
      <main className="flex-grow py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-3xl font-raleway font-extrabold text-gray-800 mb-8">
            Assessment History
          </h1>
          {assessments.length === 0 ? (
            <div className="bg-white rounded-xl p-6 shadow-home-hero text-center">
              <p className="text-lg font-montserrat text-gray-700">
                You haven't attempted any assessments yet.
              </p>
              <button
                onClick={() => navigate('/assessments')}
                className="mt-4 px-6 py-3 bg-blue-600 text-white rounded-full font-montserrat text-sm hover:bg-blue-700 transition duration-300"
              >
                Explore Assessments
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-home-hero p-6">
              <h2 className="text-xl font-raleway font-semibold text-gray-800 mb-4">
                Your Assessments
              </h2>
              <div className="overflow-x-auto">
                <table className="min-w-full table-auto">
                  <thead>
                    <tr className="bg-gray-200 text-gray-700">
                      <th className="px-4 py-3 text-left font-montserrat text-sm font-semibold">
                        Assessment Title
                      </th>
                      <th className="px-4 py-3 text-left font-montserrat text-sm font-semibold">
                        Last Attempt
                      </th>
                      <th className="px-4 py-3 text-left font-montserrat text-sm font-semibold">
                        Attempts Made
                      </th>
                      <th className="px-4 py-3 text-left font-montserrat text-sm font-semibold">
                        Attempts Remaining
                      </th>
                     
                      <th className="px-4 py-3 text-left font-montserrat text-sm font-semibold">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {assessments.map((assessment, index) => {
                      console.log(`Assessment: ${assessment.title}, Result Mode: ${assessment.resultMode}`);
                      return (
                        <React.Fragment key={assessment.slug}>
                          <tr
                            className={`border-b ${
                              index === 0 ? 'bg-blue-50' : 'hover:bg-gray-50'
                            } ${assessment.attemptsMade > 0 ? 'cursor-pointer' : ''}`}
                            onClick={() => toggleAssessmentAttempts(assessment.slug, assessment.attemptsMade)}
                          >
                            <td className="px-4 py-3 text-gray-700 font-montserrat text-sm">
                              {assessment.title}
                              {index === 0 && (
                                <span className="ml-2 inline-block bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                                  Recent
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-gray-700 font-montserrat text-sm">
                              {assessment.lastAttempt
                                ? new Date(assessment.lastAttempt).toLocaleString()
                                : 'Not attempted'}
                            </td>
                            <td className="px-4 py-3 text-gray-700 font-montserrat text-sm">
                              {assessment.attemptsMade}
                            </td>
                            <td className="px-4 py-3 text-gray-700 font-montserrat text-sm">
                              {assessment.remainingTries}
                            </td>
                           
                            <td className="px-4 py-3 text-gray-700 font-montserrat text-sm">
                              <div className="flex space-x-2">
                                {assessment.remainingTries === 0 ? (
                                  <span className="text-gray-700 font-montserrat text-sm">
                                    Completed
                                  </span>
                                ) : (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleViewAssessment(assessment.slug);
                                    }}
                                    className="px-3 py-1 bg-blue-600 text-white rounded-full font-montserrat text-sm hover:bg-blue-700 transition duration-300"
                                    title="Take Assessment"
                                  >
                                    <i className="fas fa-play mr-1"></i> Take
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {expandedAssessment === assessment.slug && assessment.attemptsMade > 0 && (
                            <tr>
                              <td colSpan={assessment.resultMode !== 1 ? 6 : 7} className="px-4 py-3 bg-gray-50">
                                <div className="pl-6">
                                  <h3 className="text-sm font-raleway font-semibold text-gray-800 mb-2">
                                    Attempt History
                                  </h3>
                                  {attemptsData[assessment.slug] ? (
                                    <table className="min-w-full table-auto">
                                      <thead>
                                        <tr className="bg-gray-100 text-gray-700">
                                          <th className="px-4 py-2 text-left font-montserrat text-xs font-semibold">
                                            Attempt Number
                                          </th>
                                          <th className="px-4 py-2 text-left font-montserrat text-xs font-semibold">
                                            Attempt Date
                                          </th>
                                          {assessment.resultMode === 1 && (
                                            <th className="px-4 py-2 text-left font-montserrat text-xs font-semibold">
                                              Score
                                            </th>
                                          )}
                                          <th className="px-4 py-2 text-left font-montserrat text-xs font-semibold">
                                            Actions
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {attemptsData[assessment.slug].length > 0 ? (
                                          attemptsData[assessment.slug].map((attempt) => (
                                            <tr
                                              key={attempt._id}
                                              className="border-b hover:bg-gray-100"
                                            >
                                              <td className="px-4 py-2 text-gray-700 font-montserrat text-xs">
                                                {attempt.attemptNumber}
                                              </td>
                                              <td className="px-4 py-2 text-gray-700 font-montserrat text-xs">
                                                {new Date(attempt.submittedAt).toLocaleString()}
                                              </td>
                                              {assessment.resultMode === 1 && (
                                                <td className="px-4 py-2 text-gray-700 font-montserrat text-xs">
                                                  {attempt.score !== null
                                                    ? `${attempt.score}/${attempt.totalQuestions}`
                                                    : 'N/A'}
                                                </td>
                                              )}
                                              <td className="px-4 py-2 text-gray-700 font-montserrat text-xs">
                                                {assessment.resultMode === 1 ? (
                                                  <button
                                                    onClick={() => handleViewResults(assessment.slug, attempt._id)}
                                                    className="px-3 py-1 bg-green-600 text-white rounded-full font-montserrat text-xs hover:bg-green-700 transition duration-300"
                                                    title="View Results"
                                                  >
                                                    <i className="fas fa-eye mr-1"></i> Result
                                                  </button>
                                                ) : (
                                                  <span className="text-gray-700 font-montserrat text-xs">
                                                    Submitted
                                                  </span>
                                                )}
                                              </td>
                                            </tr>
                                          ))
                                        ) : (
                                          <tr>
                                            <td
                                              colSpan={assessment.resultMode === 1 ? 4 : 3}
                                              className="px-4 py-2 text-gray-700 font-montserrat text-xs text-center"
                                            >
                                              No attempt details available.
                                            </td>
                                          </tr>
                                        )}
                                      </tbody>
                                    </table>
                                  ) : (
                                    <div className="text-gray-700 font-montserrat text-xs text-center">
                                      Loading attempt details...
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AssessmentDashboard;